/**
 * ResetPassword.tsx
 * Password reset confirmation page - user sets new password after clicking email link
 * 
 * Supabase redirects here with tokens in the URL hash after user clicks reset link.
 * The AuthContext will automatically pick up the session from the URL.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

// Password validation rules
const passwordRules = [
  { id: 'length', label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p: string) => /[0-9]/.test(p) },
];

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { updatePassword, session } = useAuth();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string>('');
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);

  // Check for recovery token in URL on mount
  useEffect(() => {
    const checkRecoveryToken = async () => {
      // Check URL hash for recovery token (Supabase PKCE flow)
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const type = params.get('type');
      const accessToken = params.get('access_token');
      
      // Also check query params
      const searchParams = new URLSearchParams(window.location.search);
      const queryType = searchParams.get('type');
      
      if (type === 'recovery' || queryType === 'recovery' || accessToken) {
        console.log('🔐 Recovery token detected, validating...');
        
        // Supabase will automatically set the session from the URL
        // Give it a moment to process
        setTimeout(async () => {
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          if (currentSession) {
            console.log('✅ Recovery session valid');
            setIsValidToken(true);
          } else {
            console.log('❌ Recovery session invalid');
            setIsValidToken(false);
          }
        }, 500);
      } else if (session) {
        // User is already logged in, allow password change
        setIsValidToken(true);
      } else {
        // No token and not logged in
        setIsValidToken(false);
      }
    };

    checkRecoveryToken();
  }, [session]);

  // Validate password strength
  const passwordValidation = passwordRules.map(rule => ({
    ...rule,
    passed: rule.test(password),
  }));
  
  const isPasswordValid = passwordValidation.every(rule => rule.passed);
  const doPasswordsMatch = password === confirmPassword && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate
    if (!isPasswordValid) {
      setError('Please meet all password requirements');
      return;
    }

    if (!doPasswordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword(password);
      setIsSuccess(true);
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state - checking token
  if (isValidToken === null) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F0F0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Invalid/expired token state
  if (isValidToken === false) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F0F0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: 3, sm: 6 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              backgroundColor: 'white',
              borderRadius: 3,
              border: '1px solid #F0F0F0',
              textAlign: 'center',
            }}
          >
            <ErrorOutlineIcon 
              sx={{ 
                fontSize: 80, 
                color: 'error.main', 
                mb: 3 
              }} 
            />
            
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Invalid or Expired Link
            </Typography>
            
            <Typography color="text.secondary" sx={{ mb: 4 }}>
              This password reset link is invalid or has expired. 
              Please request a new one.
            </Typography>
            
            <Button
              component={RouterLink}
              to="/forgot-password"
              variant="contained"
              sx={{ mb: 2 }}
            >
              Request New Link
            </Button>
            
            <Link component={RouterLink} to="/login" variant="body2">
              Back to Sign In
            </Link>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F0F0 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={0}
            sx={{
              padding: { xs: 3, sm: 6 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              backgroundColor: 'white',
              borderRadius: 3,
              border: '1px solid #F0F0F0',
              textAlign: 'center',
            }}
          >
            <CheckCircleOutlineIcon 
              sx={{ 
                fontSize: 80, 
                color: 'success.main', 
                mb: 3 
              }} 
            />
            
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Password Reset Successful!
            </Typography>
            
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your password has been updated. You'll be redirected to the dashboard shortly.
            </Typography>
            
            <CircularProgress size={24} sx={{ mb: 2 }} />
            
            <Link component={RouterLink} to="/dashboard" variant="body2">
              Go to Dashboard now
            </Link>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Password reset form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F0F0 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 3, sm: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: 3,
            border: '1px solid #F0F0F0',
          }}
        >
          {/* CHECK SET GO Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              marginBottom: 4,
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              alt="CHECK SET GO Logo"
              sx={{
                width: 60,
                height: 72,
                objectFit: 'contain',
              }}
            />
            <Box>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 800, 
                  lineHeight: 0.9,
                  color: 'text.primary',
                }}
              >
                CHECK SET GO
              </Typography>
            </Box>
          </Box>

          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            Set New Password
          </Typography>
          
          <Typography color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Create a strong password for your account.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password requirements checklist */}
            {password.length > 0 && (
              <List dense sx={{ mb: 1 }}>
                {passwordValidation.map((rule) => (
                  <ListItem key={rule.id} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {rule.passed ? (
                        <CheckIcon color="success" fontSize="small" />
                      ) : (
                        <CloseIcon color="error" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={rule.label}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: rule.passed ? 'success.main' : 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              error={confirmPassword.length > 0 && !doPasswordsMatch}
              helperText={
                confirmPassword.length > 0 && !doPasswordsMatch 
                  ? 'Passwords do not match' 
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading || !isPasswordValid || !doPasswordsMatch}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Reset Password'}
            </Button>
            
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/login" variant="body2">
                Back to Sign In
              </Link>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;

